import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { fechaCorta, horaLocal, horaLocalDeCorreccion, NOMBRES_TIPO } from '@/lib/fichajes';

/**
 * Corregir (admin) o pedir que se corrija (la propia persona) un día del
 * registro de jornada. Es el mismo formulario en los dos casos:
 *
 *   modo="corregir"  POST /fichajes/correcciones: se escribe en el registro.
 *   modo="pedir"     POST /fichajes/solicitudes: queda pendiente hasta que el
 *                    admin la apruebe.
 *
 * En ninguno se borra nada: se añaden fichajes o se anulan los que están mal,
 * con un motivo obligatorio.
 *
 * `jornadas`: [{ jornada_id, fecha_local, entrada, eventos: [{id, tipo, ts, origen}] }]
 */
export function DialogoCorreccion({
  modo, abierto, alCerrar, persona, fecha, jornadas, zona, alGuardar,
}) {
  const [anular, setAnular] = useState([]);
  const [jornadaId, setJornadaId] = useState(null);
  const [nuevos, setNuevos] = useState([]);
  const [tipo, setTipo] = useState('salida');
  const [hora, setHora] = useState('');
  const [dia, setDia] = useState('');
  const [entrada, setEntrada] = useState('');
  const [salida, setSalida] = useState('');
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!abierto) return;
    setAnular([]);
    setNuevos([]);
    setMotivo('');
    setHora('');
    setEntrada('');
    setSalida('');
    setTipo('salida');
    setDia(fecha || '');
    setJornadaId(jornadas[0]?.jornada_id || null);
  }, [abierto, jornadas, fecha]);

  const pedir = modo === 'pedir';
  const jornada = jornadas.find((j) => j.jornada_id === jornadaId);

  const anadirNuevo = () => {
    if (!hora) return;
    setNuevos([...nuevos, { tipo, hhmm: hora }]);
    setHora('');
  };

  const guardar = async () => {
    if (!motivo.trim()) {
      toast.error(pedir ? 'Explica qué pasó' : 'El motivo es obligatorio');
      return;
    }
    let eventos;
    if (jornada) {
      const entradaHHMM = horaLocal(jornada.entrada, zona);
      eventos = nuevos.map((n) => ({
        tipo: n.tipo,
        jornada_id: jornada.jornada_id,
        hora_local: horaLocalDeCorreccion(jornada.fecha_local, n.hhmm, entradaHHMM),
      }));
    } else {
      if (!dia || !entrada) {
        toast.error('Pon el día y al menos la hora de entrada');
        return;
      }
      eventos = [{ tipo: 'entrada', hora_local: `${dia}T${entrada}` }];
      if (salida) eventos.push({ tipo: 'salida', hora_local: horaLocalDeCorreccion(dia, salida, entrada) });
    }
    if (!eventos.length && !anular.length) {
      toast.error(pedir ? 'La solicitud no pide ningún cambio' : 'La corrección no cambia nada');
      return;
    }
    setGuardando(true);
    try {
      if (pedir) {
        const r = await axiosInstance.post('/fichajes/solicitudes', {
          fecha: jornada ? jornada.fecha_local : dia, eventos, anular, motivo: motivo.trim(),
        });
        toast.success(r.data.email_enviado
          ? 'Enviada. Tu responsable ha recibido un aviso.'
          : 'Enviada. Tu responsable la verá en el registro de jornada.');
      } else {
        const r = await axiosInstance.post('/fichajes/correcciones', {
          user_id: persona.id, eventos, anular, motivo: motivo.trim(),
        });
        toast.success(r.data.email_enviado
          ? 'Corregido. Se le ha avisado por email.'
          : 'Corregido, pero el email de aviso no ha salido.');
      }
      alGuardar();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se ha podido guardar');
    } finally {
      setGuardando(false);
    }
  };

  const titulo = pedir
    ? (jornada ? `Pedir corrección · ${fechaCorta(jornada.fecha_local)}` : 'Pedir que añadan un día')
    : `${persona?.name || ''} · ${fecha ? fechaCorta(fecha) : ''}`;

  return (
    <Dialog open={abierto} onOpenChange={(v) => !v && alCerrar()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          <DialogDescription>
            {pedir
              ? 'Tu responsable la revisará. Si la aprueba, se añade a tu registro sin borrar nada y te llega un email.'
              : 'Lo que hay no se borra: se añaden fichajes o se anulan, con el motivo, y la persona recibe un email con el cambio.'}
          </DialogDescription>
        </DialogHeader>

        {jornadas.length > 0 ? (
          <div className="space-y-4">
            {jornadas.length > 1 && (
              <div className="flex gap-2 flex-wrap">
                {jornadas.map((j) => (
                  <Button key={j.jornada_id} size="sm"
                    variant={j.jornada_id === jornadaId ? 'default' : 'outline'}
                    onClick={() => { setJornadaId(j.jornada_id); setNuevos([]); setAnular([]); }}>
                    Jornada de las {horaLocal(j.entrada, zona)}
                  </Button>
                ))}
              </div>
            )}
            {jornada && (
              <div className="space-y-1">
                <p className="text-sm font-semibold">Fichajes de esta jornada</p>
                {jornada.eventos.map((e) => (
                  <label key={e.id} className="flex items-center justify-between gap-3 text-sm py-1">
                    <span>
                      <span className="font-mono tabular-nums mr-2">{horaLocal(e.ts, zona)}</span>
                      {NOMBRES_TIPO[e.tipo] || e.tipo}
                      {e.origen === 'correccion' && <span className="text-slate-500"> (corregido)</span>}
                    </span>
                    <span className="flex items-center gap-2 text-xs text-slate-600">
                      {pedir ? 'Está mal' : 'Anular'}
                      <Checkbox
                        checked={anular.includes(e.id)}
                        onCheckedChange={(v) => setAnular(v ? [...anular, e.id] : anular.filter((x) => x !== e.id))}
                      />
                    </span>
                  </label>
                ))}
              </div>
            )}
            <div className="space-y-2 border-t pt-3">
              <p className="text-sm font-semibold">{pedir ? 'Lo que falta' : 'Añadir a esta jornada'}</p>
              <div className="flex gap-2">
                <select
                  value={tipo}
                  onChange={(e) => setTipo(e.target.value)}
                  className="border rounded-md px-2 text-sm h-10 bg-white"
                >
                  <option value="salida">Salida</option>
                  <option value="pausa_inicio">Inicio de pausa</option>
                  <option value="pausa_fin">Fin de pausa</option>
                </select>
                <Input type="time" value={hora} onChange={(e) => setHora(e.target.value)} className="w-32" />
                <Button type="button" variant="outline" onClick={anadirNuevo} disabled={!hora}>Añadir</Button>
              </div>
              {nuevos.map((n, i) => (
                <p key={i} className="text-sm text-slate-700">
                  + {NOMBRES_TIPO[n.tipo]} a las {n.hhmm}{' '}
                  <button type="button" className="text-red-600 text-xs underline"
                    onClick={() => setNuevos(nuevos.filter((_, k) => k !== i))}>quitar</button>
                </p>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {pedir ? (
              <div>
                <Label htmlFor="c-dia">Día</Label>
                <Input id="c-dia" type="date" value={dia} onChange={(e) => setDia(e.target.value)} />
              </div>
            ) : (
              <p className="text-sm text-slate-700">Este día no tiene ninguna jornada. Añádela:</p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="c-entrada">Entrada</Label>
                <Input id="c-entrada" type="time" value={entrada} onChange={(e) => setEntrada(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="c-salida">Salida</Label>
                <Input id="c-salida" type="time" value={salida} onChange={(e) => setSalida(e.target.value)} />
              </div>
            </div>
            <p className="text-xs text-slate-500">Una salida anterior a la entrada se toma como del día siguiente.</p>
          </div>
        )}

        <div>
          <Label htmlFor="c-motivo">{pedir ? '¿Qué pasó? *' : 'Motivo *'}</Label>
          <Textarea id="c-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)}
            placeholder={pedir
              ? 'Por ejemplo: me quedé sin batería y salí a las 18:00'
              : 'Por ejemplo: olvidó fichar la salida; confirmado con el cliente'} />
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={alCerrar}>Cancelar</Button>
          <Button onClick={guardar} disabled={guardando}>
            {guardando ? 'Enviando…' : pedir ? 'Enviar solicitud' : 'Guardar corrección'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
